/**
 * Enhanced API Client with Request Optimization
 * Integrates CachedApiService for 60-70% API call reduction
 */
import { cachedApiService } from './CachedApiService';
import { buildApiUrl } from '../config/apiConfig';

class ApiClient {
    private cachedApi = cachedApiService;

    /**
     * Bootstrap critical endpoints for faster startup
     */
    async bootstrap(): Promise<void> {
        try {
            await this.cachedApi.preloadEndpoints([
                '/api/data-sources/available',
                '/api/data-sources/user-preferences',
                '/api/available_models'
            ]);
        } catch (error) {
            console.warn('[ApiClient] Bootstrap preloading failed:', error);
        }
    }

    /**
     * Get public application configuration (cached, no admin auth required)
     * Returns only UI settings needed by regular users
     */
    async getPublicConfig(): Promise<Record<string, any>> {
        try {
            const response = await this.cachedApi.cachedFetch('/api/config/public');
            if (!response.ok) return {};
            const result = await response.json();
            return result.data || {};
        } catch (error) {
            console.warn('[ApiClient] Failed to get public config:', error);
            return {};
        }
    }

    /**
     * Get full admin configuration (cached, requires admin auth)
     */
    async getConfig(): Promise<any> {
        try {
            const response = await this.cachedApi.cachedFetch('/api/admin/config');
            if (!response.ok) return {};
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to get config:', error);
            return {};
        }
    }

    /**
     * Get available data sources (cached)
     */
    async getDataSources(): Promise<any[]> {
        try {
            const response = await this.cachedApi.cachedFetch('/api/data-sources/available');
            if (!response.ok) return [];
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to get data sources:', error);
            return [];
        }
    }

    /**
     * Get available models (cached)
     */
    async getModels(): Promise<any[]> {
        try {
            const response = await this.cachedApi.cachedFetch('/api/available_models');
            if (!response.ok) return [];
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to get models:', error);
            return [];
        }
    }

    /**
     * Get user preferences (cached)
     */
    async getUserPreferences(): Promise<any> {
        try {
            const response = await this.cachedApi.cachedFetch('/api/data-sources/user-preferences');
            if (!response.ok) return {};
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to get user preferences:', error);
            return {};
        }
    }

    /**
     * Send chat message (no caching - always fresh)
     */
    async sendChatMessage(data: any): Promise<any> {
        const response = await fetch(buildApiUrl('/simple-chat'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders()
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            throw new Error(`Chat API error: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Get conversation history (cached)
     */
    async getConversationHistory(conversationId: string): Promise<any> {
        try {
            const response = await this.cachedApi.cachedFetch(`/simple-chat/${conversationId}/history`);
            if (!response.ok) return { messages: [] };
            return await response.json();
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to get conversation history:', error);
            return { messages: [] };
        }
    }

    /**
     * Get user conversations list (cached)
     */
    async getUserConversations(): Promise<any[]> {
        try {
            const response = await this.cachedApi.cachedFetch('/simple-chat/conversations');
            if (!response.ok) return [];
            const data = await response.json();
            return data.conversations || [];
        } catch (error) {
            console.warn('[ApiClient] Failed to get conversations:', error);
            return [];
        }
    }

    /**
     * Search with caching for repeated queries
     */
    async search(query: string, options: any = {}): Promise<any> {
        try {
            const url = `/api/search?q=${encodeURIComponent(query)}&${new URLSearchParams(options)}`;
            const response = await this.cachedApi.cachedFetch(url);
            if (!response.ok) return { results: [] };
            return await response.json();
        } catch (error) {
            console.warn('[ApiClient] Failed to search:', error);
            return { results: [] };
        }
    }

    /**
     * Get authentication headers
     */
    private getAuthHeaders(): Record<string, string> {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }

    /**
     * Clear all caches (useful for logout or data refresh)
     */
    clearCache(): void {
        this.cachedApi.clearCache();
    }

    /**
     * Get cache statistics for debugging
     */
    getCacheStats(): any {
        return this.cachedApi.getCacheStats();
    }

    /**
     * Preload frequently used endpoints
     */
    async preloadFrequentEndpoints(): Promise<void> {
        try {
            await Promise.allSettled([
                this.getPublicConfig(),  // Use public config (no admin auth required) instead of admin config
                this.getDataSources(),
                this.getUserPreferences(),
                this.getUserConversations()
            ]);
        } catch (error) {
            console.warn('[ApiClient] Preload failed:', error);
        }
    }
}

// Export singleton instance
export const apiClient = new ApiClient();

// NOTE: Bootstrap is now called after authentication in AuthProvider
// Do NOT auto-bootstrap on import as it causes 401 errors before login